'use client'

import React, { useState } from 'react'
import Image from 'next/image'
import Link from 'next/link'
import { Offer } from '@/services/dataTypes'
import { calculateOfferDuration, discardHTMLTags, getBaseImageUrl, getCurrencySymbol, getFinalDiscountTag, getMerchantHref, getProductDetailHref } from '@/constants/hooks'
import OfferOutUrl from '@/components/shared/OfferOutUrl'
import { Star, ShoppingBag, ShoppingCart, Copy, Check, ShieldCheck, Tag, Clock, ChevronDown, ChevronRight, ExternalLink, FileText, CheckCircle2, Layers, Store, Sparkles, Zap } from 'lucide-react'
import CommentSection from './CommentSection'
import EventOfferCard from './EventOfferCard'

interface Props {
    product: Offer
    merchantRes: any
    categories: any[]
    relatedProducts: any[]
    companyDomain: string
    companyId: string
    storeSlug: string
    slugType: string
    currentMerchantSlug: string
    categorySlug?: string
}

const ProductDetailInteractive = ({
    product,
    merchantRes,
    categories,
    relatedProducts,
    companyDomain,
    companyId,
    storeSlug,
    slugType,
    currentMerchantSlug,
    categorySlug
}: Props) => {
    const originalPrice = product?.original_price ? parseFloat(product.original_price) : 0
    const salePrice = product?.sale_price ? parseFloat(product.sale_price) : 0
    const hasDiscount = originalPrice > 0 && salePrice > 0 && originalPrice > salePrice
    const discountPercent = hasDiscount ? Math.round(((originalPrice - salePrice) / originalPrice) * 100) : null
    const finalDiscountTag = getFinalDiscountTag(product?.offer_title, discountPercent)
    const savings = hasDiscount ? (originalPrice - salePrice).toFixed(2) : null
    const currencySymbol = getCurrencySymbol(product?.currency)

    const duration = calculateOfferDuration(product?.end_date)
    const isLifetime = !product?.end_date || duration === 'Life Time'

    const merchantName = product?.merchant?.merchant_name || merchantRes?.merchant_name || currentMerchantSlug
    const merchantLogo = product?.merchant?.merchant_logo || merchantRes?.merchant_logo
    const merchantHref = getMerchantHref(product?.merchant || merchantRes, storeSlug, slugType)
    const formattedCategory = product?.category?.name || 'Products'

    // Combine FAQs from product or merchant
    const allFaqs = Array.isArray(product?.faqs) && product.faqs.length > 0
        ? product.faqs
        : (Array.isArray(merchantRes?.faqs) ? merchantRes.faqs : [])

    const [openFaq, setOpenFaq] = useState<number | null>(null)

    // Product image
    const mainProductImage = getBaseImageUrl(companyDomain, product?.product_image || merchantLogo, '')

    // Interactive copy state
    const [copied, setCopied] = useState<boolean>(false)

    const handleCopyCode = async () => {
        if (!product?.coupon_code) return
        try {
            await navigator.clipboard.writeText(product.coupon_code)
            setCopied(true)
            setTimeout(() => setCopied(false), 2000)
        } catch {
            const el = document.createElement('textarea')
            el.value = product.coupon_code
            document.body.appendChild(el)
            el.select()
            document.execCommand('copy')
            document.body.removeChild(el)
            setCopied(true)
            setTimeout(() => setCopied(false), 2000)
        }
    }

    const cleanDetail = product?.offer_detail ? discardHTMLTags(product.offer_detail) : ''
    const ratingVal = 4.8

    return (
        <div className="min-h-screen bg-[#f8fafc] pb-16 font-sans">
            {/* ━━━ MAIN CONTAINER ━━━ */}
            <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-5 pb-6 space-y-4">

                {/* ━━━ In-Page Breadcrumb ━━━ */}
                <nav aria-label="Breadcrumb" className="flex items-center gap-1.5 text-xs font-medium text-slate-500 overflow-x-auto whitespace-nowrap scrollbar-hide py-1">
                    <Link href="/" className="hover:text-slate-900 transition-colors flex items-center gap-1 shrink-0">
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                        </svg>
                        Home
                    </Link>
                    <ChevronRight size={12} className="text-slate-300 shrink-0" />
                    <Link href="/products" className="hover:text-slate-900 transition-colors shrink-0">Products</Link>
                    <ChevronRight size={12} className="text-slate-300 shrink-0" />
                    <Link href={merchantHref} className="hover:text-slate-900 transition-colors shrink-0">{merchantName}</Link>
                    {product?.category?.slug && (
                        <>
                            <ChevronRight size={12} className="text-slate-300 shrink-0" />
                            <Link href={`/products/${currentMerchantSlug}/${product.category.slug}`} className="hover:text-slate-900 transition-colors shrink-0">
                                {formattedCategory}
                            </Link>
                        </>
                    )}
                    <ChevronRight size={12} className="text-slate-300 shrink-0" />
                    <span className="text-slate-800 font-semibold truncate max-w-[200px] sm:max-w-xs">
                        {discardHTMLTags(product?.offer_title)}
                    </span>
                </nav>

                {/* ━━━ HERO SHOWCASE GRID ━━━ */}
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 items-start pt-1">

                    {/* LEFT COLUMN: Product Showcase Image Card */}
                    <div className="lg:col-span-5">
                        <div className="relative bg-white rounded-2xl border border-slate-200 p-4 sm:p-6 shadow-xs overflow-hidden flex items-center justify-center min-h-[250px] sm:min-h-[280px] group">
                            {/* Floating Discount Badge */}
                            {(finalDiscountTag || discountPercent) && (
                                <div className="absolute top-3 left-3 z-10">
                                    <span className="inline-flex items-center gap-1 text-[10px] font-black text-emerald-800 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-md uppercase tracking-wider shadow-2xs">
                                        <Tag size={10} className="text-emerald-700" /> {finalDiscountTag || `${discountPercent}% OFF`}
                                    </span>
                                </div>
                            )}

                            {/* Floating Duration / Expiry Badge */}
                            {duration && !isLifetime && (
                                <div className="absolute top-3 right-3 z-10">
                                    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-md text-[11px] font-bold bg-amber-50 text-amber-800 border border-amber-200 shadow-2xs">
                                        <Clock size={11} className="text-amber-700" /> {duration}
                                    </span>
                                </div>
                            )}

                            {/* Product Image with Zoom */}
                            <div className="relative w-full h-[180px] sm:h-[220px] flex items-center justify-center">
                                <Image
                                    src={mainProductImage}
                                    alt={discardHTMLTags(product.offer_title)}
                                    fill
                                    className="object-contain transition-transform duration-300 group-hover:scale-105"
                                    priority
                                    unoptimized
                                />
                            </div>
                        </div>
                    </div>
                    {/* RIGHT COLUMN: Product Title, Pricing & CTAs */}
                    <div className="lg:col-span-7 space-y-3.5">

                        {/* Top Badges & Product Title */}
                        <div className="space-y-1.5">
                            <div className="flex items-center gap-2 flex-wrap">
                                <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-md border border-emerald-200">
                                    <ShieldCheck size={11} className="text-emerald-600" /> Verified Deal
                                </span>
                                {product?.offer_type?.name && (
                                    <span className="text-[10px] font-semibold text-slate-500 bg-slate-100 px-2 py-0.5 rounded-md border border-slate-200/60 capitalize">
                                        {product.offer_type.name}
                                    </span>
                                )}
                            </div>

                            <h1 className="text-lg sm:text-xl md:text-2xl font-black text-slate-900 leading-snug tracking-tight max-w-xl">
                                {discardHTMLTags(product?.offer_title)}
                            </h1>
                        </div>

                        {/* Rating Row */}
                        <div className="flex items-center gap-2">
                            <div className="flex items-center gap-0.5">
                                {[1, 2, 3, 4, 5].map((s) => (
                                    <Star
                                        key={s}
                                        size={14}
                                        className="text-amber-400 fill-amber-400"
                                    />
                                ))}
                            </div>
                            <span className="text-xs font-bold text-slate-700">{ratingVal}</span>
                        </div>

                        {/* ── Price Block (Theme 5 Minimal Style) ── */}
                        {(salePrice > 0 || originalPrice > 0) && (
                            <div className="space-y-2 pt-0.5">
                                <div className="flex items-baseline gap-3 flex-wrap">
                                    {salePrice > 0 ? (
                                        <span className="text-2xl sm:text-3xl font-black text-slate-900 leading-none tracking-tight">
                                            {currencySymbol}{salePrice.toFixed(2)}
                                        </span>
                                    ) : (
                                        <span className="text-2xl sm:text-3xl font-black text-slate-900 leading-none tracking-tight">
                                            {currencySymbol}{originalPrice.toFixed(2)}
                                        </span>
                                    )}
                                    {originalPrice > 0 && salePrice > 0 && (
                                        <span className="text-sm text-slate-400 line-through font-medium">
                                            {currencySymbol}{originalPrice.toFixed(2)}
                                        </span>
                                    )}
                                    {discountPercent && discountPercent > 0 && (
                                        <span className="inline-flex items-center text-[10px] font-black text-emerald-800 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-md">
                                            {discountPercent}% OFF
                                        </span>
                                    )}
                                </div>

                                {/* Savings callout */}
                                {savings && (
                                    <div className="flex items-center gap-1.5 px-2.5 py-1 bg-emerald-50 border border-emerald-100 rounded-lg w-fit">
                                        <CheckCircle2 size={13} className="text-emerald-600 shrink-0" />
                                        <span className="text-xs font-semibold text-emerald-800">
                                            You save <span className="font-black">{currencySymbol}{savings}</span>
                                        </span>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Action Buttons: Buy Now & Visit Store (Auto Width / Compact) */}
                        <div className="flex items-center flex-wrap gap-2.5 pt-0.5">
                            <OfferOutUrl
                                unique_id={product?.unique_id}
                                outUrl={product?.url}
                                merchantHref={merchantHref}
                                domain={companyDomain}
                                customClass="w-fit inline-flex items-center justify-center gap-1.5 px-5 h-10 bg-[#071822] hover:bg-[#00d69f] hover:text-[#071822] text-white font-bold text-xs uppercase tracking-wider rounded-xl transition-all duration-200 shadow-md shadow-[#071822]/15 active:scale-95 cursor-pointer shrink-0"
                            >
                                <ShoppingBag size={14} />
                                {product?.coupon_code ? 'Get Code & Buy Now' : 'Buy Now'}
                            </OfferOutUrl>

                            <OfferOutUrl
                                unique_id={product?.unique_id}
                                outUrl={product?.url}
                                merchantHref={merchantHref}
                                domain={companyDomain}
                                customClass="w-fit inline-flex items-center justify-center gap-1.5 px-4 h-10 bg-white hover:bg-slate-50 text-slate-700 border border-slate-200 font-bold text-xs uppercase tracking-wider rounded-xl transition-all active:scale-95 cursor-pointer shrink-0"
                            >
                                <ShoppingCart size={14} />
                                Visit Store
                            </OfferOutUrl>
                        </div>

                        {/* Coupon Code Section (if present) */}
                        {product?.coupon_code && (
                            <div className="bg-slate-50 border border-dashed border-slate-300 p-3 sm:p-3.5 rounded-xl flex items-center justify-between gap-3 shadow-2xs max-w-md">
                                <div>
                                    <span className="text-[10px] font-black uppercase tracking-wider text-slate-500 flex items-center gap-1">
                                        <Tag size={11} /> Promo Code
                                    </span>
                                    <p className="text-base sm:text-lg font-mono font-black text-slate-900 tracking-wider mt-0.5 select-all">
                                        {product.coupon_code}
                                    </p>
                                </div>
                                <button
                                    onClick={handleCopyCode}
                                    className={`px-4 py-2 rounded-lg text-xs font-bold uppercase tracking-wider transition-all duration-200 flex items-center gap-1.5 cursor-pointer shadow-xs active:scale-95 ${copied
                                        ? 'bg-emerald-600 text-white'
                                        : 'bg-slate-900 hover:bg-slate-800 text-white'
                                        }`}
                                >
                                    {copied ? <Check size={13} /> : <Copy size={13} />}
                                    {copied ? 'Copied!' : 'Copy Code'}
                                </button>
                            </div>
                        )}

                        {/* Trust & Guarantee Indicators (From Theme 5) */}
                        <div className="flex items-center gap-3 flex-wrap text-[10px] font-semibold text-slate-400 pt-1">
                            <span className="inline-flex items-center gap-1 text-emerald-700 font-bold">
                                <ShieldCheck size={12} className="text-emerald-600" /> 100% Verified
                            </span>
                            <span>•</span>
                            <span className="inline-flex items-center gap-1 text-slate-600 font-medium">
                                <Zap size={11} className="text-amber-500" /> Direct Link
                            </span>
                            <span>•</span>
                            <span className="inline-flex items-center gap-1 text-slate-600 font-medium">
                                <Clock size={11} className="text-slate-400" /> Daily Updated
                            </span>
                        </div>
                    </div>
                </div>

                {/* LOWER SECTION: Product Description, FAQs & Sidebar */}
                <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">

                    {/* ── LEFT COLUMN (lg:col-span-8): Description & FAQs ── */}
                    <div className="lg:col-span-8 space-y-6">
                        {/* Full Product Description (Complete, No Truncation) */}
                        {cleanDetail && (
                            <div className="bg-white border border-slate-200 rounded-2xl p-5 sm:p-6 shadow-xs space-y-3">
                                <div className="flex items-center gap-2 pb-2.5 border-b border-slate-100">
                                    <FileText size={16} className="text-[#00bf8d]" />
                                    <h2 className="text-xs font-bold uppercase tracking-wider text-slate-900">
                                        Product Description & Overview
                                    </h2>
                                </div>

                                <div className="text-xs sm:text-sm text-slate-800 leading-relaxed">
                                    {product.offer_detail ? (
                                        <div
                                            className="prose prose-slate prose-xs sm:prose-sm max-w-none text-slate-800 leading-relaxed"
                                            dangerouslySetInnerHTML={{ __html: product.offer_detail }}
                                        />
                                    ) : (
                                        <p>{cleanDetail}</p>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* FAQs Section */}
                        {allFaqs.length > 0 && (
                            <div className="bg-white rounded-2xl border border-slate-200 p-5 sm:p-6 shadow-xs space-y-3">
                                <div className="flex items-center justify-between pb-2.5 border-b border-slate-100">
                                    <h3 className="text-xs font-bold uppercase tracking-wider text-slate-900 flex items-center gap-1.5">
                                        Frequently Asked Questions
                                    </h3>
                                    <span className="text-[10px] font-bold text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full">
                                        {allFaqs.length} FAQs
                                    </span>
                                </div>
                                <div className="space-y-2">
                                    {allFaqs.map((faq: any, idx: number) => (
                                        <details
                                            key={faq.id || idx}
                                            className="group border border-slate-200 rounded-xl p-3 transition-all duration-200 open:bg-slate-50/50"
                                        >
                                            <summary className="text-xs font-bold text-slate-800 cursor-pointer list-none flex items-center justify-between gap-2">
                                                <span>{faq.question || faq.title}</span>
                                                <ChevronDown size={14} className="text-slate-400 group-open:rotate-180 transition-transform duration-200 shrink-0" />
                                            </summary>
                                            <div
                                                className="text-xs text-slate-800 mt-2 pt-2 border-t border-slate-200 leading-relaxed prose prose-slate prose-xs max-w-none"
                                                dangerouslySetInnerHTML={{ __html: faq.answer || faq.description || '' }}
                                            />
                                        </details>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* ── RIGHT COLUMN (lg:col-span-4): Sidebar (Store Profile, Categories, Reviews) ── */}
                    <div className="lg:col-span-4 space-y-5 lg:sticky lg:top-6">
                        {/* 1. Official Store Quick Deal Card (From Theme 5) */}
                        <div className="bg-white rounded-2xl border border-slate-200 p-4 shadow-xs space-y-3">
                            <div className="flex items-center gap-3">
                                {merchantLogo && (
                                    <div className="w-11 h-11 rounded-xl bg-slate-50 border border-slate-200/80 p-1 flex items-center justify-center shrink-0 shadow-2xs">
                                        <Image
                                            src={getBaseImageUrl(companyDomain, merchantLogo, '')}
                                            alt={merchantName}
                                            width={36}
                                            height={36}
                                            className="object-contain"
                                            unoptimized
                                        />
                                    </div>
                                )}
                                <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-1.5">
                                        <p className="text-xs font-bold text-slate-900 truncate">{merchantName}</p>
                                        <ShieldCheck size={12} className="text-emerald-600 shrink-0" />
                                    </div>
                                    <p className="text-[10px] text-slate-400">Official Store Partner</p>
                                </div>
                            </div>

                            <div className="h-px bg-slate-100" />

                            <OfferOutUrl
                                unique_id={product?.unique_id}
                                outUrl={product?.url}
                                merchantHref={merchantHref}
                                domain={companyDomain}
                                customClass="w-full flex items-center justify-center gap-1.5 bg-[#071822] hover:bg-[#00d69f] hover:text-[#071822] text-white font-bold text-[11px] uppercase tracking-wider py-2.5 rounded-xl transition-all shadow-xs active:scale-[0.98] cursor-pointer"
                            >
                                <Store size={13} />
                                Visit Official Store
                            </OfferOutUrl>

                            <Link
                                href={merchantHref}
                                className="block text-center text-[11px] font-semibold text-slate-500 hover:text-[#00a87a] hover:underline transition-colors"
                            >
                                View all {merchantName} coupons & deals →
                            </Link>
                        </div>

                        {/* 2. Shop by Category / Related Categories Card (From Theme 5) */}
                        {Array.isArray(categories) && categories.length > 0 && (
                            <div className="bg-white rounded-2xl border border-slate-200 shadow-xs overflow-hidden">
                                <div className="px-4 py-2.5 border-b border-slate-100 bg-slate-50/50 flex items-center gap-1.5">
                                    <Layers size={13} className="text-[#00bf8d]" />
                                    <h3 className="text-[11px] font-bold text-slate-700 uppercase tracking-wider">
                                        Shop by Category
                                    </h3>
                                </div>
                                <div className="p-2 divide-y divide-slate-50">
                                    {categories.slice(0, 8).map((cat: any, i: number) => (
                                        <Link
                                            key={cat.slug || i}
                                            href={`/products/${currentMerchantSlug}/${cat.slug}`}
                                            className="flex items-center justify-between py-2 px-2.5 rounded-lg hover:bg-slate-50 group transition-colors"
                                        >
                                            <span className="text-[11px] font-medium text-slate-600 group-hover:text-slate-900 transition-colors truncate">
                                                {cat.name}
                                            </span>
                                            <ChevronRight size={13} className="text-slate-300 group-hover:text-slate-600 transition-colors shrink-0" />
                                        </Link>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* 3. Customer Reviews & Comment Section */}
                        <div id="reviews-section" className="bg-white rounded-2xl border border-slate-200 p-4 shadow-xs">
                            <CommentSection
                                offer_id={product?.unique_id || ''}
                                company_id={companyId}
                            />
                        </div>
                    </div>
                </div>


                {/* SIMILAR PRODUCTS (Same Category) */}
                {relatedProducts?.length > 0 && (
                    <div className="mt-12 space-y-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <h2 className="text-base sm:text-lg font-bold text-slate-900 tracking-tight">
                                    Similar Deals in <span className="text-[#00a87a]">{formattedCategory}</span>
                                </h2>
                                <p className="text-xs text-slate-400 mt-0.5">
                                    Handpicked top deals and discounts
                                </p>
                            </div>
                            {product?.category?.slug && (
                                <Link
                                    href={`/products/${currentMerchantSlug}/${product.category.slug}`}
                                    className="text-xs font-bold text-[#00a87a] hover:underline flex items-center gap-1"
                                >
                                    <span>View all</span>
                                    <ExternalLink size={12} />
                                </Link>
                            )}
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                            {relatedProducts.slice(0, 4).map((item: any, i: number) => (
                                <EventOfferCard
                                    key={i}
                                    product={item}
                                    merchantHref={getMerchantHref(merchantRes, storeSlug, slugType)}
                                    domain={companyDomain}
                                    merchant_name={merchantRes?.merchant_name}
                                    merchant_logo={merchantRes?.merchant_logo}
                                    productDetailUrl={getProductDetailHref(merchantRes, slugType, item?.slug)}
                                />
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    )
}

export default ProductDetailInteractive
